import { User as Auth0User } from '@auth0/auth0-react';
import { Candidate } from './candidates.types';
import { ApiListPracticeProspect, PracticeProspect, ProspectQuestion } from './prospect.types';
import { Scorecard } from './scorecard.types';

// -- ENUMS --
export enum CallProcessingStatus {
  PROCESSING = 'PROCESSING',
  PROCESSED = 'PROCESSED',
  PROCESSING_FAILED = 'PROCESSING_FAILED',
}

export enum CallSortingFilters {
  CALL_DURATION = 'callDuration',
  CALLER = 'caller',
  PROSPECT = 'prospect',
  START_TIME = 'startTime',
  POINTS_PERCENTAGE = 'pointsPercentage',
}

export enum CallSpeaker {
  AGENT = 'AGENT',
  CUSTOMER = 'CUSTOMER',
}

export enum CallMethod {
  WEB_CALL = 'WEB_CALL',
  TELEPHONY = 'TELEPHONY',
}

export enum CallKind {
  SIMULATION = 'SIMULATION',
  REAL = 'REAL',
}

type WordBase = {
  word: string;
  start: number;
  end: number;
  confidence: number;
  punctuated_word?: string;
  speaker?: number;
  speaker_confidence?: number;
};

export type Utterance = {
  start: number;
  end: number;
  transcript: string;
  speaker: CallSpeaker;
  channel: number;
  id: string;
  words: WordBase[];
};

// -- TYPES --
export type CallSpeechCategory = {
  id: string;
  callSid: string;
  summary: string;
  speaker: CallSpeaker;
  category: string;
  label: string;
  importanceConfidence: number;
  startTime: number;
  endTime: number;
  createdAt: Date;
  updatedAt: Date;
};

export enum CallStatus {
  STARTED = 'started',
  ENDED = 'ended',
}

type CallChapter = {
  id: string;
  callSid: string;
  title: string;
  startTime: number;
  endTime: number;
};

export type CallCoaching = {
  overallFeedback: string;
  strengths: string[];
  areasOfImprovement: string[];
  nextSteps: string[];
};

type ProspectQuestionAnswer = {
  id: string;
  question: ProspectQuestion;
  isCorrect: boolean | null;
};

type CallQuestionsScorecard = {
  id: string;
  points: number;
  percentage: number;
  answers: ProspectQuestionAnswer[];
};

type BaseCall = {
  // Required fields
  callSid: string;
  callKind: CallKind;
  orgId: string;
  startTime: Date;
  createdAt: Date;
  updatedAt: Date;
  // Optional fields
  userId?: string | null;
  user?: Auth0User;
  endTime?: Date | null;
  callDuration?: number | null; // Duration in seconds
  audioPath?: string | null;
  transcriptionPath?: string | null;
  processingStatus?: CallProcessingStatus | null;
  scoringStatus?: CallProcessingStatus | null;
  coachingStatus?: CallProcessingStatus | null;
  questionsScoringStatus?: CallProcessingStatus | null;
  summary?: string | null;
  wordsCount?: number | null;
  fillerWordsCount?: number | null;
  talkTime?: number | null;
  longestMonologueStart?: number | null;
  longestMonologueDuration?: number | null;
  aiCoachingResult?: string | null;

  callChapters?: CallChapter[];
  callSpeechCategories?: CallSpeechCategory[];

  // Flagging details
  flaggedUserIds?: string[];
  isFlaggedToUser?: boolean;

  // Call score
  // If scorecard is saved from get calls api, it will only have the pointsPercentage.
  // If scorecard is saved from websocket event response,
  // it will have the full scorecard object to populate realtime.
  scorecard?:
    | {
        pointsPercentage: number | null;
      }
    | Scorecard;

  // Call comments
  numberOfComments?: number;
  usersCommented?: {
    id: string;
    name: string;
    picture: string;
  }[];

  // Scored questions
  questionsScorecard?: CallQuestionsScorecard | null;
};

type SimulationCallWithoutProspect = BaseCall & {
  callKind: CallKind.SIMULATION;
  // Required fields
  personaId: string;
  receivingPhoneNumber: string;
  callType: string;
  callStatus: string; // "inbound" or "outbound"
  // Optional fields
  candidateId?: string | null;
  candidate?: Candidate;
  endedReason?: string | null;
  incomingPhoneNumber?: string | null;
  isSuccessfulOutcome?: boolean | null;
  callMethod?: CallMethod | null;
  attemptNumber?: number | null;
};

export type SimulationCall = SimulationCallWithoutProspect & {
  practiceProspect: PracticeProspect;
};

export type RealCall = BaseCall & {
  callKind: CallKind.REAL;
  // Required fields
  name: string;
  userId: string;
  creationStatus: CallProcessingStatus;
};

export type Call = SimulationCall | RealCall;

export type SimulationCallWithApiListPracticeProspect = SimulationCallWithoutProspect & {
  practiceProspect: ApiListPracticeProspect;
};

export type FlaggedUser = {
  userId: string;
  notify: boolean;
};

export enum MeetingState {
  // Call not started.
  IDLE = 'IDLE',
  // Extra state used for web calls done over WebSockets,
  // because the call does not start until the mic is connected.
  CONNECTING_TO_MIC = 'CONNECTING_TO_MIC',
  // Call started.
  JOINING_MEETING = 'JOINING_MEETING',
  // Call connected.
  JOINED_MEETING = 'JOINED_MEETING',
  // Call hanging up.
  HANGING_UP = 'HANGING_UP',
  // Call error.
  ERROR = 'ERROR',
}
